// BonusActivityTimeline.tsx - Main Timeline Component

import React, { useState, useMemo, useEffect } from 'react';
import {
    JobDetailBonusStatusTimelineResult,
    BonusStatusTimelineEvent,
    BonusStatusTimelineEventType,
    BonusStatusTimelineFilters,
    BonusType
} from '../../providers/types';
import { BonusStatusTimelineEventCard } from './BonusStatusTimelineEventCard';
import { BonusStatusCard } from './BonusStatusCard';
import { BonusStatusTimelineFiltersPanel } from './BonusStatusTimelineFiltersPanel';
import { useBonusStatusTimeline } from '../../hooks/useBonusStatusTimeline';
import './BonusStatusTimeline.css';

interface BonusStatusTimelineProps {
    jobId: number;
    onEventCountChange?: (count: number) => void;
}

export const BonusStatusTimeline: React.FC<BonusStatusTimelineProps> = ({
    jobId,
    onEventCountChange
}) => {

    const { data, isLoading, error, refetch } = useBonusStatusTimeline(jobId);

    const [selectedBonusType, setSelectedBonusType] = useState<number | undefined>(undefined);
    const [filters, setFilters] = useState<BonusStatusTimelineFilters>({});
    const [bonusType, setBonusType] = useState<string>(
        localStorage.getItem("cba.bonusType") || "ClosingBonus"
    );
    // Get job number and project number from summaries
    const jobNumber = data?.bonusStatusSummaries[0]?.jobNumber || '';
    const projectNumber = data?.bonusStatusSummaries[0]?.projectNumber || '';

    // Get summaries by type
    const stage5Summary = useMemo(
        () => data?.bonusStatusSummaries.find(s => s.bonusTypeId === BonusType.Stage5),
        [data?.bonusStatusSummaries]
    );

    const closingSummary = useMemo(
        () => data?.bonusStatusSummaries.find(s => s.bonusTypeId === BonusType.Closing),
        [data?.bonusStatusSummaries]
    );

    // Filter events based on selected criteria
    const filteredEvents = useMemo(() => {
        if (!data?.bonusStatusTimelineEvents) return [];

        const filtered = data.bonusStatusTimelineEvents.filter(event => {
            // Bonus type filter
            if (selectedBonusType && event.bonusTypeId !== selectedBonusType) {
                return false;
            }

            // Role filter
            if (filters.roleTypeId && event.roleName) {
                // This would need role type ID mapping in real implementation
                return true;
            }

            // Event type filter
            if (filters.eventType && event.eventType !== filters.eventType) {
                return false;
            }

            // Date range filter
            if (filters.dateFrom && event.eventTimestamp < filters.dateFrom) {
                return false;
            }
            if (filters.dateTo && event.eventTimestamp > filters.dateTo) {
                return false;
            }

            return true;
        });

        // Sort by most recent first (descending order)
        return filtered.sort((a, b) => b.eventTimestamp.getTime() - a.eventTimestamp.getTime());
    }, [data?.bonusStatusTimelineEvents, selectedBonusType, filters]);

    // Count events by bonus type
    const stage5Count = useMemo(
        () => data?.bonusStatusTimelineEvents.filter(e => e.bonusTypeId === BonusType.Stage5).length || 0,
        [data?.bonusStatusTimelineEvents]
    );

    const closingCount = useMemo(
        () => data?.bonusStatusTimelineEvents.filter(e => e.bonusTypeId === BonusType.Closing).length || 0,
        [data?.bonusStatusTimelineEvents]
    );

    const totalEventCount = stage5Count + closingCount;

    useEffect(() => {
        onEventCountChange?.(totalEventCount);
    }, [totalEventCount, onEventCountChange]);

    if (isLoading) {
        return (
            <div className="timeline-loading">
                <div className="spinner" />
                <p>Loading bonus activity...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="timeline-error">
                <h3>Error Loading Timeline</h3>
                <p>{error}</p>
                <button onClick={refetch} className="btn-retry">Retry</button>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="timeline-empty">
                <p>No bonus activity found for this job.</p>
            </div>
        );
    }

    return (
        <div className="bonus-activity-timeline">
            {/* Header */}
            <div className="timeline-header">
                <div className="timeline-title">
                    <h2>Bonus Activity Timeline</h2>
                    {/* <span className="job-info">
                        Job #{jobNumber} • Project #{projectNumber}
                    </span> */}
                </div>
            </div>

            {/* Current Status Summary Cards */}
            <div className="status-summary-cards">
                {stage5Summary && (
                    <BonusStatusCard
                        summary={stage5Summary}
                        isActive={selectedBonusType === BonusType.Stage5 || !selectedBonusType}
                        onClick={() => setSelectedBonusType(
                            selectedBonusType === BonusType.Stage5 ? undefined : BonusType.Stage5
                        )}
                    />
                )}
                {closingSummary && (
                    <BonusStatusCard
                        summary={closingSummary}
                        isActive={selectedBonusType === BonusType.Closing || !selectedBonusType}
                        onClick={() => setSelectedBonusType(
                            selectedBonusType === BonusType.Closing ? undefined : BonusType.Closing
                        )}
                    />
                )}
            </div>

            {/* Bonus Type Tabs */}
            <div className="bonus-type-tabs">
                {(bonusType && bonusType === 'ClosingBonus' && <button
                    className={`tab ${!selectedBonusType ? 'active' : ''}`}
                    onClick={() => setSelectedBonusType(undefined)}
                >
                    All Activity
                    <span className="badge">{stage5Count + closingCount}</span>
                </button>
                )}
                <button
                    className={`tab ${!selectedBonusType || selectedBonusType === BonusType.Stage5 ? 'active' : ''}`}
                    onClick={() => setSelectedBonusType(BonusType.Stage5)}
                >
                    Stage5
                    <span className="badge">{stage5Count}</span>
                </button>
                {(bonusType && bonusType === 'ClosingBonus' &&
                    <button
                        className={`tab ${!selectedBonusType || selectedBonusType === BonusType.Closing ? 'active' : ''}`}
                        onClick={() => setSelectedBonusType(BonusType.Closing)}
                    >
                        Closing
                        <span className="badge">{closingCount}</span>
                    </button>)}
            </div>

            {/* Filters */}
            {/* <BonusStatusTimelineFiltersPanel
                filters={filters}
                onFiltersChange={setFilters}
            /> */}

            {/* Timeline Events */}
            <div className="timeline-container">
                {filteredEvents.length === 0 ? (
                    <div className="timeline-empty-state">
                        <p>No events match the selected filters.</p>
                    </div>
                ) : (
                    <div className="timeline-events">
                        {filteredEvents.map((event, index) => (
                            <BonusStatusTimelineEventCard
                                key={`${event.eventType}-${event.statusHistoryId || event.commentId}-${index}`}
                                event={event}
                                isLast={index === filteredEvents.length - 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
